import { DestroyRef, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { select, Store } from "@ngrx/store";
import { forkJoin } from "rxjs";
import { filter, take } from "rxjs/operators";

import {
	ClearCustomer,
	FinishAppInitializer,
	IAppCommonState,
	LoadCustomerInfo,
	LoadInitializationInfo,
	selectInitializationFailed,
	StartAppInitializer,
} from "@guest-web/core";
import { ignoreNil } from "@triparound/trp-components/shared";

import { CompanyFacadeService } from "./company-facade.service";
import { IntegrationsService } from "./integrations.service";
import { ThemeService } from "./theme.service";

@Injectable()
export class InitializationService {
	uUidsArray = [];

	private uuidRegEx = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/gi;

	constructor(
		private store: Store<IAppCommonState>,
		private companyService: CompanyFacadeService,
		private themeService: ThemeService,
		private integrationsService: IntegrationsService,
		private destroyRef: DestroyRef,
	) {
		const fullPath = window.location.pathname;
		const pathArray = fullPath.split("/");

		for (const value of pathArray) {
			this.uuidRegEx.lastIndex = 0; // regexp keeps state when using 'g'
			if (this.uuidRegEx.test(value)) {
				this.uUidsArray.push(value);
			}
		}
	}

	init(): Promise<boolean> {
		this.store.dispatch(new StartAppInitializer());

		this.loadInitializationInfo();

		const companyInfo$ = this.companyService.organizationInfo$.pipe(ignoreNil(), take(1));

		const initializationFailed$ = this.store.pipe(
			select(selectInitializationFailed),
			filter(value => value !== null),
			take(1),
		);

		const themeConfig$ = this.themeService.configuration$.pipe(ignoreNil(), take(1));

		return new Promise(resolve => {
			forkJoin([companyInfo$, themeConfig$, initializationFailed$])
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(([companyInfo]) => {
					this.integrationsService.setIntegrationActions(companyInfo?.integrations || []);
					this.themeService.setTheme();
					this.store.dispatch(new FinishAppInitializer());
					resolve(true);
				});
		});
	}

	loadInitializationInfo(): void {
		const companyUuid = this.getCompanyUuid();
		this.store.dispatch(
			new LoadInitializationInfo({
				companyUuid,
			}),
		);
	}

	loadCustomerInfo(customerUuid: string): void {
		this.store.dispatch(
			new LoadCustomerInfo({
				customerUuid,
			}),
		);
	}

	clearCustomerInfo(): void {
		this.store.dispatch(new ClearCustomer());
	}

	private getCompanyUuid(): string {
		let orgUuid = "";

		if (this.uUidsArray.length > 0) {
			const value = this.uUidsArray[0];
			this.uuidRegEx.lastIndex = 0; // regexp keeps state when using 'g'
			if (this.uuidRegEx.test(value)) {
				orgUuid = value;
			}
		}

		return orgUuid;
	}
}
